import React, { useMemo, useState } from 'react';
import { monthLabels, dayLabels, useCalendar } from 'pages/Collaborations/CalendarPage';
import { ReactComponent as ArrowRight } from '../../icons/arrow-right.svg';
import { ReactComponent as ArrowLeft } from '../../icons/arrow-left.svg';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';

type ChangeMonthProps = [number, boolean];
type DateProps = {
  day: number,
  month: number,
  year: number,
};

const CalendarDayStyled = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    height: 34px;
    width: 34px;
    cursor: pointer;
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    color: ${({ theme }) => theme.colors.primaryTextColor};
    &:hover {
        // .1 opacity
        background-color: ${({ theme }) => theme.colors.primaryColor + '1A'};
    }
    &.active {
        // .25 opacity
        background-color: ${({ theme }) => theme.colors.primaryColor + '40'};
        color: #FFF;
    }
`;

const CalendarWeekdayWrapper = styled.div`
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    grid-auto-rows: 1fr;
    grid-gap: 8px;
    margin-bottom: 35px;
`;

const CalendarWrapper = styled.div`
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    grid-auto-rows: 1fr;
    grid-gap: 8px;
    flex: 1 0 auto;
`;

export default function CalendarSmall() {
  const { selectedDate } = useCalendar();
  const currentDate = { day: selectedDate.getDate(), month: selectedDate.getMonth(), year: selectedDate.getFullYear() };
  const [date, setDate] = useState<DateProps>({ day: selectedDate.getDate(), month: selectedDate.getMonth(), year: selectedDate.getFullYear() });
  const theme = useTheme();

  // Get the first day's of the month weekday (0-6)
  let firstDayWeekday = new Date(date.year, date.month, 1).getDay() - 1;
  if (firstDayWeekday === -1) firstDayWeekday = 6;
  // Get the last day's of the month weekday (0-6)
  let lastDayWeekday = new Date(date.year, date.month + 1, 0).getDay() - 1;
  if (lastDayWeekday === -1) lastDayWeekday = 6;

  // Next month [0-11, true/false (should change year)]
  const nextMonth: ChangeMonthProps = useMemo(() => date.month === 11 ? [0, true] : [date.month + 1, false], [date.month]);
  // Previous month [0-11, true/false (should change year)]
  const prevMonth: ChangeMonthProps = useMemo(() => date.month === 0 ? [11, true] : [date.month - 1, false], [date.month]);
  const daysInMonth = new Date(date.year, date.month + 1, 0).getDate();

  const handleNextMonth = () => {
    setDate((prev) => {
      if (prev.month === 11) {
        return {
          day: prev.day,
          month: 0,
          year: prev.year + 1,
        };
      }
      return {
        day: prev.day,
        month: prev.month + 1,
        year: prev.year,
      };
    });
  };
  const handlePrevMonth = () => {
    setDate((prev) => {
      if (prev.month === 0) {
        return {
          day: prev.day,
          month: 11,
          year: prev.year - 1,
        };
      }
      return {
        day: prev.day,
        month: prev.month - 1,
        year: prev.year,
      };
    });
  };

    type CalendarDayProps = {
      day: number,
      disabled?: boolean,
      month: number,
    };

    const CalendarDay = ({ day, disabled, month }: CalendarDayProps) => {
      return (
            <div style={{ display:'flex', justifyContent:'center', alignItems:'center' }}>
                <CalendarDayStyled className={month === currentDate.month && currentDate.day === day && currentDate.year === date.year ? 'active' : ''} style={{ color: disabled ? theme.colors.gray : theme.colors.primaryTextColor }}>
                    {day}
                </CalendarDayStyled>
            </div>
      );
    };

    return (
        <>
            <div className='dashboard-calendar-month-wrapper'>
                <ArrowLeft className='svg-black' onClick={handlePrevMonth} style={{ cursor: 'pointer' }} />
                <span>{monthLabels[date.month]} {date.year}</span>
                <ArrowRight className='svg-black' onClick={handleNextMonth} style={{ cursor: 'pointer' }} />
            </div>
            <div className='dashboard-calendar-wrapper'>
                <CalendarWeekdayWrapper>
                    {dayLabels.map((day) => (
                        <div className='dashboard-calendar-weekday' key={day}>
                            {day}
                        </div>
                    ))}
                </CalendarWeekdayWrapper>
                <CalendarWrapper>
                    {/* Map month days from first day of the week, if it isn't a 0, then map days from previous month */}
                    {Array.from(Array(firstDayWeekday === 0 ? 0 : firstDayWeekday).keys()).map((day) => (
                        <CalendarDay key={day} disabled month={prevMonth[0]} day={new Date(date.year, date.month, 0).getDate() - day} />
                    ))}
                    {/* Map month days */}
                    {Array.from(Array(daysInMonth).keys()).map((day) => (
                        <CalendarDay month={date.month} key={day} day={day + 1} />
                    ))}
                    {/* Map month days from last day of the week, if it isn't a 6, then map days from next month using lastDayWeekday */}
                    {Array.from(Array(lastDayWeekday === 6 ? 0 : 6 - lastDayWeekday).keys()).map((day) => {
                      
                      return (
                        <CalendarDay month={nextMonth[0]} key={day} disabled day={day + 1} />
                      );
                    })}
                    {/* If there is only 5 rows, then add 7 more days from the next month */}
                    {daysInMonth + firstDayWeekday + 6 - lastDayWeekday <= 35 && Array.from(Array(7).keys()).map((day) => (
                        <CalendarDay month={nextMonth[0]} key={day} disabled day={day + (6 - lastDayWeekday) + 1} />
                    ))}
                    {daysInMonth + firstDayWeekday + 6 - lastDayWeekday + 7 <= 35 && Array.from(Array(7).keys()).map((day) => (
                        <CalendarDay month={nextMonth[0]} key={day} disabled day={day + (6 - lastDayWeekday) + 7 + 1} />
                    ))}
                </CalendarWrapper>
            </div>
        </>
    );

}